<template>
  <div>
    <AppDialog
      class="holiday-setup-dialog"
      title="Add holiday"
      data-testid="holiday-wizard-dialog"
      :show="showDialog"
      :loading="loading"
      append-to-body
      position="centered"
      @close="closeAllDialogs"
    >
      <AppWizard
        :value="step"
        :steps="wizardSteps"
      >
        <ValidationObserver ref="dialogObserver">
          <AppWizardStep
            v-show="step === 0"
            :show-back-button="false"
            :show-delete-button="false"
            @previous="previousStep"
            @next="nextStep"
            @tab-selected="changeTab"
            @close="closeAllDialogs"
          >
            <ValidationObserver ref="HolidaySetupFormObserver">
              <HolidaySetupForm
                :holiday="holiday"
                :predefined-holidays="predefinedHolidays"
                :organization-tz="organizationTz"
                @holiday-updated="handleHolidayUpdated"
                @members-updated="handleMembersUpdated"
              />
            </ValidationObserver>
          </AppWizardStep>
          <AppWizardStep
            v-show="step === 1"
            show-back-button
            is-last-step
            :save-button-text="saveButtonText"
            :save-button-disabled="saveButtonDisabled"
            @previous="previousStep"
            @tab-selected="changeTab"
            @input="save"
            @close="closeAllDialogs"
          >
            <ValidationObserver ref="HolidayMembersSetupFormObserver">
              <HolidayMembersSetupForm
                :holiday="holiday"
                :organization-tz="organizationTz"
                :overlap-shift-count="overlapShiftCount"
                :organization-id="organizationId"
                :members-reload="membersReload"
                :country-list="countryList"
                @disable-save-button="handleDisableSaveButton"
                @holiday-updated="handleHolidayUpdated"
                @members-updated="handleMembersUpdated"
              />
            </ValidationObserver>
          </AppWizardStep>
        </ValidationObserver>
      </AppWizard>
    </AppDialog>

    <ShiftOverlapConfirmationDialog
      :show="showShiftOverlapConfirmationDialog"
      :overlap-shift-count="overlapShiftCount"
      :saving="saving"
      @update-remove-shift-field="handleRemoveShiftField"
      @close="closeDialog"
    />
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { HolidaysResource } from '~/resources';
import HolidayMembersSetupForm from '~/components/holidays/HolidayMembersSetupForm.vue';
import HolidaySetupForm from '~/components/holidays/HolidaySetupForm.vue';
import ShiftOverlapConfirmationDialog from '~/components/ShiftOverlapConfirmationDialog.vue';
import HolidayMixin from '~/mixins/holidays/HolidayMixin';

export default {
  name: 'HolidayWizard',
  components: {
    HolidayMembersSetupForm,
    HolidaySetupForm,
    ShiftOverlapConfirmationDialog,
  },
  mixins: [HolidayMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    redirectPath: {
      type: String,
      default: null,
    },
    loadDataOnCreation: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      step: 0,
      holiday: {
        date: markRaw(moment.tz(this.organizationTz)),
        recurring: false,
        auto_add_new_users: false,
        holiday_user_ids: [],
        remove_shifts: false,
        members_added_via: null,
        membership_rules: { countries: [], employment_types: [] },
      },
      membersListData: {},
      predefinedHolidays: {},
      showShiftOverlapConfirmationDialog: false,
      overlapShiftCount: 0,
      showDialog: this.show,
      saving: false,
      overlapShiftError: false,
      loading: false,
      disableSaveButton: false,
      membersReload: false,
    };
  },
  computed: {
    wizardSteps() {
      return [{ title: 'Set up holiday' }, { title: 'Assign members' }];
    },
    saveButtonText() {
      return this.saving ? 'Saving...' : 'Save';
    },
  },
  async created() {
    if (this.loadDataOnCreation) {
      await this.fetch();
    }
  },
  methods: {
    async fetch() {
      try {
        this.loading = true;
        this.membersReload = false;
        const params = { organizationId: this.organizationId };
        const response = await HolidaysResource.new(params, null, { abortReload: true });

        if (response && response.predefined_holidays) {
          this.predefinedHolidays = response.predefined_holidays;
          this.loading = false;
        } else {
          throw new Error('Unable to fetch data for the holiday creation.');
        }
      } catch (error) {
        this.closeAllDialogs();
        await this.$appMessage('Unable to fetch data for the holiday creation. Try again later.');
      }
    },
    async loadInitialData() {
      this.showDialog = true;
      await this.fetch();
    },
    closeAllDialogs() {
      this.showDialog = false;
      this.showShiftOverlapConfirmationDialog = false;
      this.$emit('close');

      if (this.$refs.dialogObserver) {
        this.$refs.dialogObserver.reset();
      }

      this.step = 0;
      this.holiday = {
        date: markRaw(moment.tz(this.organizationTz)),
        recurring: false,
        auto_add_new_users: false,
        holiday_user_ids: [],
        remove_shifts: false,
        members_added_via: null,
        membership_rules: { countries: [], employment_types: [] },
      };
      this.membersListData = {};
      this.saving = false;
      this.membersReload = true;
      this.overlapShiftCount = 0;
      this.loading = false;
      this.disableSaveButton = false;
    },
    previousStep() {
      this.step--;
    },
    nextStep() {
      this.$refs.HolidaySetupFormObserver.validate().then((result) => {
        if (result) {
          this.validate().then((valid) => {
            if (valid) {
              this.step++;
            }
          });
        }
      });
    },
    changeTab(tabId) {
      const id = Number(tabId.replace('tab-', ''));

      this.step = id;
    },
    async handleRemoveShiftField(field, value) {
      this.handleHolidayUpdated(field, value);
      this.save();
    },
    async save() {
      this.performHolidayAction('create');
    },
    async createHoliday() {
      try {
        const params = { organizationId: this.organizationId };
        const holidayParams = {
          ...this.holiday,
          date: moment(this.holiday.date).format('YYYY-MM-DD'),
        };
        const response = await HolidaysResource.create(
          params,
          {
            holiday: holidayParams,
            members_data: this.membersListData,
            redirect_path: this.redirectPath,
            ...(this.overlapShiftError && { overlap_shifts_error: this.overlapShiftError }),
          },
          { abortReload: true },
        );

        if (response.success) {
          this.closeAllDialogs();
          window.location.assign(response.url);
        } else if (response.errors) {
          // Server errors must be set on the observer that OWNS each field: vee-validate v4's
          // Form.setErrors only reaches its own fields and does NOT propagate into nested observers.
          // Step-1 fields (name/amount_used/symbolic_type/date) live in HolidaySetupFormObserver;
          // step-2 fields (membership_rules) live in HolidayMembersSetupFormObserver. Setting the full
          // error bag on both lets each render what it owns — e.g. the duplicate-name error on step 1
          // AND the server's "at least one rule must be selected" on step 2. (Each observer simply
          // ignores keys it has no field for.)
          this.$refs.HolidaySetupFormObserver.setErrors(response.errors);
          this.$refs.HolidayMembersSetupFormObserver.setErrors(response.errors);

          const errorsToCheckOnResponse = ['name', 'amount_used', 'symbolic_type', 'date'];
          const backToTheFirstStep = errorsToCheckOnResponse.some((key) => key in response.errors);

          if (backToTheFirstStep) {
            this.step = 0;
          }

          this.showShiftOverlapConfirmationDialog = false;
          this.showDialog = true;

          this.$appMessage('The holiday cannot be created.');
        } else {
          throw new Error('Unable to fetch data for the holiday creation.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.showShiftOverlapConfirmationDialog = false;
          this.showDialog = true;
          this.$appMessage('Unexpected error. Please try again.');
        }
      } finally {
        this.saving = false;
      }
    },
    async validate() {
      const params = { organizationId: this.organizationId };
      const payload = {
        holiday: {
          symbolic_type: this.holiday.symbolic_type,
          name: this.holiday.name,
          amount_used: this.holiday.amount_used,
          date: moment(this.holiday.date).format('YYYY-MM-DD'),
        },
      };
      const response = await HolidaysResource.validate(params, payload, { abortReload: true });

      // The validated fields (symbolic_type, name, amount_used, date) are registered with the
      // step-1 observer, not the outer dialogObserver. With vee-validate v4 a <Field> registers
      // with its NEAREST <Form>, and Form.setErrors only reaches its own fields — so setting them
      // on dialogObserver (which owns no direct fields, only nested observers) silently dropped
      // server errors like "name has already been taken", making Next look like a no-op.
      this.$refs.HolidaySetupFormObserver.setErrors(response.errors);

      return response.valid;
    },
  },
};
</script>
